// The friendly marks: what an own hull and a squadmate hull wear beside the
// silhouette (R185, R830, ui-map-interface.md §6.1).
//
// The Marks body of a theme carries two fields here. `ownFleet` picks the
// mark on the commander's own hull and `squadFleet` picks the mark on a
// squadmate's hull, and every value comes from mockup round twelve
// (`archive/mockups/012-friendly-markers.html`, series A and B).
//
// **Two values change the HULL and add nothing beside it.**
// `white-core` (card A05, the own AUTO value) draws the own hull white, a
// little larger, over the axial bloom; `hollow-hull` (card B05) gives a
// squadmate the stroked master where the commander has a filled one.
// Neither has a cell here, because neither is a mark of its own.
// `tint-only` (card B01) is the same kind of answer: it lifts the faction
// colour a quarter toward white and adds no ink at all. The squad AUTO
// value is `trailing-dot` (R1279), which is a mark with a cell.
//
// **Every other value is one baked cell beside the hull.** The cell is
// baked WHITE at the hull's own supersampling, and the quad carries the
// tint and the alpha, so one cell serves every faction, every zoom and
// every fade. A cell says whether it is square to the SCREEN, which never
// turns, or square to the COURSE, which takes the hull's heading.
//
// **The slot numeral (card B09) has no cell of its own.** It draws the
// damage figure's glyph cell, which the atlas already bakes, at the group
// badge's height. It is the one value that puts text on the canvas, and
// R830 carries the argument for the exception.
//
// This is a leaf (`leaves.test.ts`): it holds the shapes and reads its
// figures from the generated pack, and it imports no engine. The layer
// that draws it is `fast/layers/fleets.ts` and the module that bakes the
// cells is `fast/gfx/atlas.ts`.

import { FLEET_MARKS } from '../gen/presentation';
import type { MarkGraphics } from './marks';
import type { OwnFleetStyle, SquadFleetStyle } from '../ui/settings';
import { TAU } from './math';

/** The key of one baked mark cell. */
export const fleetMarkKey = (style: string, part: string) => `fmark:${style}:${part}`;

/** Which ink a mark cell wears: the white of an own mark, or the faction. */
export type MarkTint = 'white' | 'faction';

/**
 * Which square a mark holds.
 *
 * A `screen` mark never turns, which is what a targeting mark must do. A
 * `course` mark takes the hull's heading, so it frames the direction of
 * travel and a column of hulls reads as a column.
 */
export type MarkSquare = 'screen' | 'course';

/** One baked cell of a mark. */
export interface FleetMarkCell {
  key: string;
  tint: MarkTint;
  alpha: number;
  square: MarkSquare;
  /** Lays the path down and paints it white. The cell carries no alpha. */
  draw(g: MarkGraphics): void;
}

/** How the slot numeral stands beside the hull (card B09). */
export interface SlotNumeral {
  /** The LEFT edge of the numeral, in map px from the hull. */
  dx: number;
  /** The MIDDLE of the numeral, in map px from the hull. y grows down. */
  dy: number;
  alpha: number;
}

/** One value of the `ownFleet` field or of the `squadFleet` field. */
export interface FleetMarkStyle {
  /** The cells drawn beside the hull. Empty for a hull treatment. */
  cells: readonly FleetMarkCell[];
  /** How far the faction colour is lifted toward white (card B01). */
  lift?: number;
  /** The squad slot printed beside the hull (card B09). */
  slot?: SlotNumeral;
}

const F = FLEET_MARKS;
const WHITE = 0xffffff;

/** The highest slot a numeral prints. A squad of more draws this one. */
export const SLOT_NUMERAL_MAX = 9;

/** A closed outline through the corners `pts`, stroked at `w`. */
function ringPoly(g: MarkGraphics, pts: number[], w: number): void {
  g.poly(pts).stroke({ width: w, color: WHITE, join: 'round' });
}

/** The four corners of a lozenge at radius `r` (cards A03 and B10). */
function diamond(r: number): number[] {
  return [0, -r, r, 0, 0, r, -r, 0];
}

/** The six corners of a hexagon at radius `r` (card A08). */
function hexagon(r: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * TAU;
    out.push(Math.cos(a) * r, Math.sin(a) * r);
  }
  return out;
}

/** One caret: arms that fall back by `h` from a point at (0, `cy`). */
function caret(g: MarkGraphics, cy: number, halfW: number, h: number, up: boolean): void {
  const back = up ? cy + h : cy - h;
  g.moveTo(-halfW, back).lineTo(0, cy).lineTo(halfW, back);
}

// ---------------------------------------------------------------------------
// The cells
// ---------------------------------------------------------------------------

/** Card A01. Two carets frame the hull along its course. */
const caretPair: FleetMarkCell = {
  key: fleetMarkKey('caret-pair', 'mark'),
  tint: 'white',
  alpha: F.caretPair.alpha,
  square: 'course',
  draw(g) {
    const f = F.caretPair;
    caret(g, -f.dy, f.halfW, f.h, true);
    caret(g, f.dy, f.halfW, f.h, false);
    g.stroke({ width: f.w, color: WHITE, cap: 'round', join: 'round' });
  },
};

/** Card A03. A lozenge around the hull, square to the screen. */
const diamondCage: FleetMarkCell = {
  key: fleetMarkKey('diamond-cage', 'mark'),
  tint: 'white',
  alpha: F.diamondCage.alpha,
  square: 'screen',
  draw: (g) => ringPoly(g, diamond(F.diamondCage.r), F.diamondCage.w),
};

/** Card A06. Four L brackets crop the hull, square to the screen. */
const viewfinder: FleetMarkCell = {
  key: fleetMarkKey('viewfinder', 'mark'),
  tint: 'white',
  alpha: F.viewfinder.alpha,
  square: 'screen',
  draw(g) {
    const { r, arm, w } = F.viewfinder;
    for (const [sx, sy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
      g.moveTo(sx * r, sy * (r - arm)).lineTo(sx * r, sy * r).lineTo(sx * (r - arm), sy * r);
    }
    g.stroke({ width: w, color: WHITE, cap: 'round', join: 'round' });
  },
};

/** Card A07, the stem. It stands above the hull and never turns. */
const pennantStem: FleetMarkCell = {
  key: fleetMarkKey('command-pennant', 'stem'),
  tint: 'white',
  alpha: F.commandPennant.alpha,
  square: 'screen',
  draw(g) {
    const f = F.commandPennant;
    g.moveTo(0, f.stemY0).lineTo(0, f.stemY1).stroke({ width: f.stemW, color: WHITE });
  },
};

/**
 * Card A07, the flag. It is the one part of any value that takes the
 * faction ink, which is why the pennant is two cells and not one.
 */
const pennantFlag: FleetMarkCell = {
  key: fleetMarkKey('command-pennant', 'flag'),
  tint: 'faction',
  alpha: F.commandPennant.alpha,
  square: 'screen',
  draw(g) {
    const f = F.commandPennant;
    g.poly([
      0, f.stemY1,
      f.flagLen, f.stemY1 + f.flagH / 2,
      0, f.stemY1 + f.flagH,
    ]).fill({ color: WHITE });
  },
};

/** Card A08. A hairline hexagon, square to the screen. */
const hexFrame: FleetMarkCell = {
  key: fleetMarkKey('hex-frame', 'mark'),
  tint: 'white',
  alpha: F.hexFrame.alpha,
  square: 'screen',
  draw: (g) => ringPoly(g, hexagon(F.hexFrame.r), F.hexFrame.w),
};

/** Card B04. One dot in the wake position, so it rides the course. */
const trailingDot: FleetMarkCell = {
  key: fleetMarkKey('trailing-dot', 'mark'),
  tint: 'white',
  alpha: F.trailingDot.alpha,
  square: 'course',
  draw: (g) => { g.circle(F.trailingDot.dx, 0, F.trailingDot.r).fill({ color: WHITE }); },
};

/** Card B06. Three dashes under the hull, square to the screen. */
const underDash: FleetMarkCell = {
  key: fleetMarkKey('under-dash', 'mark'),
  tint: 'faction',
  alpha: F.underDash.alpha,
  square: 'screen',
  draw(g) {
    const f = F.underDash;
    for (const k of [-1, 0, 1] as const) {
      g.moveTo(k * f.pitch - f.half, f.dy).lineTo(k * f.pitch + f.half, f.dy);
    }
    g.stroke({ width: f.w, color: WHITE, cap: 'round' });
  },
};

/** Card B10. Card A03's lozenge at a quarter of its weight. */
const faintLozenge: FleetMarkCell = {
  key: fleetMarkKey('faint-lozenge', 'mark'),
  tint: 'faction',
  alpha: F.faintLozenge.alpha,
  square: 'screen',
  draw: (g) => ringPoly(g, diamond(F.faintLozenge.r), F.faintLozenge.w),
};

/** A value that changes the hull alone, so it bakes no cell. */
const NO_CELLS: readonly FleetMarkCell[] = [];

// ---------------------------------------------------------------------------
// The two tables
// ---------------------------------------------------------------------------

/**
 * The mark on the commander's own hull, per value of the `ownFleet` field.
 *
 * `white-core` is the shipped look and the AUTO value: the white tint, the
 * 1.05 scale and the bloom of `packHulls` and `packBlooms`. Every ALTERNATE
 * drops all three — the hull keeps its plain faction colour — and draws its
 * own mark instead, because a value must replace the AUTO drawing and never
 * stack on it.
 */
export const OWN_FLEET_MARKS: Record<OwnFleetStyle, FleetMarkStyle> = {
  'white-core': { cells: NO_CELLS },
  'caret-pair': { cells: [caretPair] },
  'diamond-cage': { cells: [diamondCage] },
  viewfinder: { cells: [viewfinder] },
  'command-pennant': { cells: [pennantStem, pennantFlag] },
  'hex-frame': { cells: [hexFrame] },
};

/**
 * The mark on a squadmate's hull, per value of the `squadFleet` field.
 *
 * `hollow-hull` is the stroked master, and it adds no cell. Every other
 * value, the AUTO value `trailing-dot` included (R1279), takes the FILLED master, so a squadmate is as solid as
 * any other hull of the faction and the value alone says it is a mate.
 */
export const SQUAD_FLEET_MARKS: Record<SquadFleetStyle, FleetMarkStyle> = {
  'hollow-hull': { cells: NO_CELLS },
  'tint-only': { cells: NO_CELLS, lift: F.squadLift },
  'trailing-dot': { cells: [trailingDot] },
  'under-dash': { cells: [underDash] },
  'slot-numeral': { cells: NO_CELLS, slot: F.slotNumeral },
  'faint-lozenge': { cells: [faintLozenge] },
};

/**
 * Every baked cell of both tables, by key.
 *
 * The atlas resolver reads this: a cell is baked on the first frame that
 * draws it, so a page bakes only the value the reader picked.
 */
export const FLEET_MARK_CELLS: ReadonlyMap<string, FleetMarkCell> = new Map(
  [...Object.values(OWN_FLEET_MARKS), ...Object.values(SQUAD_FLEET_MARKS)]
    .flatMap((s) => s.cells)
    .map((c) => [c.key, c]),
);

// ---------------------------------------------------------------------------
// The squad slot
// ---------------------------------------------------------------------------

/** What `squadSlotsOf` needs of one clan member. */
export interface SquadMember {
  accountId: number;
  /** The SQUAD the member is in, 1 to 5. Zero is unassigned. */
  squad: number;
}

/**
 * The squad slot of every member of one squad, by account id (R830).
 *
 * Nothing on the wire carries a slot. `ClanMember.squad` names the squad a
 * member is in and not a place inside it, so the client makes the number:
 * the slot is the 1-based rank of the account id among the members of that
 * squad, sorted by account id. The rank is therefore the same number for
 * every viewer of the squad, and it changes only when the squad changes.
 *
 * Squad 0 is "unassigned" and has no slots, so it returns an empty map.
 */
export function squadSlotsOf(
  members: readonly SquadMember[], squad: number,
): ReadonlyMap<number, number> {
  const out = new Map<number, number>();
  if (squad === 0) return out;
  const ids = members.filter((m) => m.squad === squad).map((m) => m.accountId);
  ids.sort((a, b) => a - b);
  for (let i = 0; i < ids.length; i++) out.set(ids[i], i + 1);
  return out;
}
